import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { NAVIGATION } from "../Config";
import Avatar from "react-avatar";
import { Link, useLocation } from "react-router-dom";
import { IoClose, IoColorPaletteOutline, IoMenu } from "react-icons/io5";
import { supabase } from "../supabaseClient";
import ThemeModal from './theme/ThemeModal.jsx';
import { normalizeAvatarSrc } from "./avatarUtils";
import { getPendingFriendRequests } from "../Data/FriendshipHelpers.jsx";

function NavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loggedUser, setLoggedUser] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [resolvedAvatarSrc, setResolvedAvatarSrc] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [pendingFriendRequestCount, setPendingFriendRequestCount] = useState(0);
  const navContainerRef = useRef(null);
  const accountMenuRef = useRef(null);
  const authUserId = loggedUser?.id || null;

  const resolveAvatarFromStorage = async (userId) => {
    if (!userId) {
      setResolvedAvatarSrc("");
      return;
    }

    const { data: files, error } = await supabase.storage
      .from("avatars")
      .list(userId, {
        limit: 50,
        sortBy: { column: "updated_at", order: "desc" },
      });

    if (error || !files?.length) {
      setResolvedAvatarSrc("");
      return;
    }

    const latestFile = files.find((file) => !file.name.endsWith("/")) || files[0];
    if (!latestFile?.name) {
      setResolvedAvatarSrc("");
      return;
    }

    const { data: publicData } = supabase.storage
      .from("avatars")
      .getPublicUrl(`${userId}/${latestFile.name}`);

    setResolvedAvatarSrc(publicData?.publicUrl || "");
  };

  async function fetchUserDetails(userId) {
    if (!userId) {

      return; // Skip fetching if no userId is provided
    }
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("public_user_id", userId)
      .single();

    if (error) {
      console.error("Error fetching user details:", error);
    } else {
      setUserDetails(data);

      const avatarPreference =
        typeof data?.avatar === "string" ? data.avatar.trim() : "";

      // Allow users to force initials fallback even if storage files exist.
      if (avatarPreference === "__initials__") {
        setResolvedAvatarSrc("");
        return;
      }

      const explicitAvatar = normalizeAvatarSrc(data?.avatar);
      if (explicitAvatar) {
        setResolvedAvatarSrc(explicitAvatar);
      } else {
        await resolveAvatarFromStorage(userId);
      }
    }
  }

  useEffect(() => {
    const init = async () => {
      const userResult = await supabase.auth.getUser();
      const authUser = userResult?.data?.user || null;

      if (authUser) {
        setLoggedUser(authUser);
        fetchUserDetails(authUser.id);
      } else {
        setLoggedUser(null);
        setUserDetails({});
        setResolvedAvatarSrc("");
      }
    };

    init();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          setLoggedUser(session.user);
          fetchUserDetails(session.user.id);
        } else {
          setLoggedUser(null);
          setUserDetails({});
          setResolvedAvatarSrc("");
        }
      }
    );

    return () => authListener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!authUserId) return;
    fetchUserDetails(authUserId);
  }, [authUserId, location.pathname]);

  useEffect(() => {
    if (!userDetails?.id) {
      setPendingFriendRequestCount(0);
      return;
    }

    let cancelled = false;
    getPendingFriendRequests(userDetails.id).then((requests) => {
      if (!cancelled) setPendingFriendRequestCount(requests.length);
    });

    return () => {
      cancelled = true;
    };
  }, [userDetails?.id, location.pathname]);

  const logout = async () => {
    await supabase.auth.signOut();
    setLoggedUser(null);
    setUserDetails({});
    navigate("/login");
  };
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        navContainerRef.current &&
        !navContainerRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }

      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target)
      ) {
        setAccountMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleNavLinkClick = () => {
    if (menuOpen) {
      setMenuOpen(false);
    }
  };

  const handleAccountNavigate = (path) => {
    setAccountMenuOpen(false);
    navigate(path);
  };

  const handleAccountLogout = async () => {
    setAccountMenuOpen(false);
    await logout();
  };

  const handleOpenTheme = () => {
    setAccountMenuOpen(false);
    setIsModalOpen(true);
  };

  const getLinkClassName = (path) => {
    const active = location.pathname === path;
    return active ? "fb-nav__link active" : "fb-nav__link";
  };

  return (
    <nav className="fb-nav">
      <div className="fb-nav__inner" ref={navContainerRef}>
        <Link className="fb-nav__brand" to={NAVIGATION.HOME} aria-label="FriendlyBets home">
          <span><img src="/favicon.png" alt="" /></span>
          <b>FriendlyBets</b>
        </Link>
        <button
          className="fb-nav__toggle"
          type="button"
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
          style={{ position: "relative" }}
        >
          {menuOpen ? <IoClose /> : <IoMenu />}
          {!menuOpen && pendingFriendRequestCount > 0 && (
            <span
              className="fb-nav__badge-dot"
              aria-label={`${pendingFriendRequestCount} pending friend requests`}
            />
          )}
        </button>

        <div className={menuOpen ? "fb-nav__links open" : "fb-nav__links"}>
          <Link
            className={getLinkClassName(NAVIGATION.HOME)}
            aria-current="page"
            to={NAVIGATION.HOME}
            onClick={handleNavLinkClick}
          >
            Home
          </Link>
          <Link
            className={getLinkClassName(NAVIGATION.ABOUT)}
            to={NAVIGATION.ABOUT}
            onClick={handleNavLinkClick}
          >
            About
          </Link>
          {loggedUser && (
            <>
              <Link
                className={getLinkClassName(NAVIGATION.MYBETS)}
                to={NAVIGATION.MYBETS}
                onClick={handleNavLinkClick}
              >
                MyBets
              </Link>
              <Link
                className={getLinkClassName(NAVIGATION.ADDFRIENDS)}
                to={NAVIGATION.ADDFRIENDS}
                onClick={handleNavLinkClick}
              >
                <span className="fb-nav__link-label">
                  Friends
                  {pendingFriendRequestCount > 0 && (
                    <span
                      className="fb-nav__badge-dot"
                      aria-label={`${pendingFriendRequestCount} pending friend requests`}
                    />
                  )}
                </span>
              </Link>
              <Link
                className={getLinkClassName(NAVIGATION.LEADERBOARD)}
                to={NAVIGATION.LEADERBOARD}
                onClick={handleNavLinkClick}
              >
                Leaderboard
              </Link>
              <Link
                className={getLinkClassName(NAVIGATION.RIVALRIES)}
                to={NAVIGATION.RIVALRIES}
                onClick={handleNavLinkClick}
              >
                Rivalries
              </Link>
              <Link
                className={getLinkClassName(NAVIGATION.CHALLENGES)}
                to={NAVIGATION.CHALLENGES}
                onClick={handleNavLinkClick}
              >
                Challenges
              </Link>
              <Link
                className={getLinkClassName(NAVIGATION.NOTIFICATIONS)}
                to={NAVIGATION.NOTIFICATIONS}
                onClick={handleNavLinkClick}
              >
                Notifications
              </Link>
            </>
          )}
        </div>

        <div className="fb-nav__actions">
          {loggedUser ? (
            <div className="fb-nav__account" ref={accountMenuRef}>
              <button
                className="fb-nav__avatar-button"
                type="button"
                onClick={() => setAccountMenuOpen((prev) => !prev)}
              >
                <Avatar
                  round={true}
                  size="40"
                  name={userDetails.username || loggedUser?.email || "User"}
                  src={resolvedAvatarSrc}
                />
              </button>

              {accountMenuOpen && (
                <div className="fb-nav__menu">
                  <button onClick={() => handleAccountNavigate("/myAccount")}>My Account</button>
                  {userDetails.is_admin && (
                    <button onClick={() => handleAccountNavigate("/UserManagement")}>Update Users</button>
                  )}
                  <button onClick={() => handleAccountNavigate("/Messages")}>Messages</button>
                  <button onClick={() => handleAccountNavigate("/fullSchedule")}>Weekly Schedule</button>
                  <button onClick={() => handleAccountNavigate("/highlights")}>Yesterday's Highlights</button>
                  <button onClick={handleOpenTheme}>
                    <IoColorPaletteOutline aria-hidden="true" /> Theme
                  </button>
                  <button onClick={handleAccountLogout}>Logout</button>
                </div>
              )}
            </div>
          ) : (
            <Link className="fb-btn fb-btn--primary" to={NAVIGATION.LOGIN}>
              Login
            </Link>
          )}
        </div>
      </div>
      <ThemeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </nav>
  );
}

export default NavBar;
